import { allocateByPercentage } from './allocate-by-percentage';
import { buildAmountsSummary } from './build-amounts-summary';
import { blocksFullTaxDeduction } from './line-split-fields';

// A copy must not carry the source's approval request or decision, nor its redaction
// identity (two lines sharing one would be selected together); _view_id is reassigned.
const KEYS_NOT_COPIED = ['Status', 'status_details', 'approver', 'approval_requested_at', 'RedactedItemId', '_view_id'];

// Every money figure a line can carry. Each one present is divided on its own.
const MONEY_FIELDS = ['Total', 'SubTotal', 'TipAllocated', 'Tax1', 'Tax2', 'Tax', 'TaxDeducted'];

const hasValue = (line, field) => line[field] !== undefined && line[field] !== null;
const readAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};
const toCents = (value) => Math.round(readAmount(value) * 100);
// Adds cent-exact amounts without float noise (0.1 + 0.2).
const addCents = (...amounts) => amounts.reduce((sum, amount) => sum + toCents(amount), 0) / 100;
// A fresh deep copy: invoice lines are JSON, and no two resulting lines may share an object.
const deepCopy = (line) => JSON.parse(JSON.stringify(line));

/**
 * The source's money, divided by the rows' percentages: one object per row.
 *
 * Every money field the source carries is divided with `allocateByPercentage`, so its
 * parts add back to it to the cent; a field the source does not carry is never added.
 * Where the source keeps one of the two relationships the editor and the server write
 * (`Total = SubTotal + TipAllocated`, invoice.js `applyExpenseAmountsToItem`; and
 * `Tax = Tax1 + Tax2`), each part keeps it too, and still adds back exactly.
 */
const allocateMoney = (source, percentages) => {
  const parts = Object.fromEntries(MONEY_FIELDS
    .filter((field) => hasValue(source, field))
    .map((field) => [field, allocateByPercentage(readAmount(source[field]), percentages)]));
  const keepsTotalRule = hasValue(source, 'Total') && hasValue(source, 'SubTotal')
    && toCents(source.Total) === toCents(source.SubTotal) + toCents(source.TipAllocated);
  const keepsTaxRule = hasValue(source, 'Tax')
    && toCents(source.Tax) === toCents(source.Tax1) + toCents(source.Tax2);

  return percentages.map((_, index) => {
    const money = Object.fromEntries(Object.entries(parts).map(([field, values]) => [field, values[index]]));
    if (keepsTotalRule) money.Total = addCents(money.SubTotal, money.TipAllocated ?? 0);
    if (keepsTaxRule) money.Tax = addCents(money.Tax1 ?? 0, money.Tax2 ?? 0);
    return money;
  });
};

/**
 * XPEN-3567: Expand one invoice line into several, one per row of a split.
 *
 * Row 1 replaces the source line at its own position (same `_view_id`); rows 2…N become
 * copies appended at the end, each with the next `_view_id`, because `_view_id` is the
 * line's index (xpens-api `normalize_list_item_view_ids`). The money is divided as
 * `allocateMoney` describes. A line that carries `amounts_summary` gets a new one per
 * resulting line, built before any save: the expense-report sync reads each line's
 * figures from it.
 *
 * The caller checks `getLineSplitBlockedKey` first, for the reason to show; this function
 * repeats only the tax deduction check, returning null rather than throwing. The input
 * list and its lines are not changed, and every resulting line is built from its own
 * deep copy of the source, so no two lines share an object.
 *
 * @param {object} params
 * @param {object[]} params.listItems - The invoice's `ListItem`.
 * @param {number} params.sourceViewId - The `_view_id` of the line to split.
 * @param {{ option: *, percentage: number }[]} params.rows - One row per resulting line.
 * @param {object} params.splitField - The line's split settings (`getLineSplitField`).
 * @param {number} [params.taxDeduction] - The invoice's `TaxDeduction`.
 * @returns {{ listItems: object[], source: object } | null} The new list and the rewritten
 *   source line, or null when the line has no split settings, the source is not found,
 *   there are fewer than two rows, or the tax deduction blocks the line.
 */
export const expandLineSplit = ({ listItems, sourceViewId, rows, splitField, taxDeduction }) => {
  const lines = Array.isArray(listItems) ? listItems : [];
  const source = lines.find((line) => line?._view_id === sourceViewId);
  if (!splitField || !source || !Array.isArray(rows) || rows.length < 2) {
    return null;
  }
  if (blocksFullTaxDeduction(source, taxDeduction)) {
    return null;
  }

  const money = allocateMoney(source, rows.map((row) => row.percentage));
  const withSummary = (line) => (hasValue(source, 'amounts_summary')
    ? { ...line, amounts_summary: buildAmountsSummary(line, taxDeduction) }
    : line);
  const buildLine = (row, index, base) => {
    // All split rows replace the original allocation. The API gives each one
    // a fresh ID and source link; copying either would attach old comments.
    delete base.line_id;
    delete base.source;
    return withSummary({
      ...base,
      ...splitField.linePatch(row.option, base),
      ...money[index],
    });
  };

  const rewrittenSource = buildLine(rows[0], 0, deepCopy(source));
  const copies = rows.slice(1).map((row, offset) => {
    const base = deepCopy(source);
    KEYS_NOT_COPIED.forEach((key) => delete base[key]);
    return { ...buildLine(row, offset + 1, base), _view_id: lines.length + offset };
  });

  return {
    listItems: [...lines.map((line) => (line === source ? rewrittenSource : line)), ...copies],
    source: rewrittenSource,
  };
};
